import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import {
  Alert,
  Button,
  Modal,
  message,
  Progress,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import commonApi from 'apis/common';
import {
  batchUploadProductAlbumPictures,
  ProductAlbumBatchUploadFile,
  ProductAlbumBatchUploadPayload,
  getProductUpdateInfoNew,
} from 'apis/pms';
import { useTranslation } from 'react-i18next';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

type FileSystemEntry = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
};

type FileSystemFileEntry = FileSystemEntry & {
  file: (
    successCallback: (file: File) => void,
    errorCallback?: (error: DOMException) => void,
  ) => void;
};

type FileSystemDirectoryEntry = FileSystemEntry & {
  createReader: () => FileSystemDirectoryReader;
};

type FileSystemDirectoryReader = {
  readEntries: (
    successCallback: (entries: FileSystemEntry[]) => void,
    errorCallback?: (error: DOMException) => void,
  ) => void;
};

type DataTransferItemWithEntry = DataTransferItem & {
  webkitGetAsEntry?: () => FileSystemEntry | null;
};

type FileCandidate = {
  file: File;
  relativePath: string;
};

type RelativeFile = ProductAlbumBatchUploadFile & {
  folderName: string;
};

type FolderRecord = {
  folderName: string;
  files: RelativeFile[];
};

type FolderFileMap = Record<string, Record<string, RelativeFile>>;

type UploadFailure = {
  folderName: string;
  reason: string;
};

type UploadSummary = {
  failures: UploadFailure[];
  successFolders: string[];
};

type UploadTaskOutcome = {
  folderName: string;
  fileIndex: number;
  url?: string;
  error?: string;
};

const DIGIT_FOLDER_REG = /^\d+$/;
const INTERNAL_FOLDER_REG = /^元画像_(\d+)$/;
const MAX_FILES_PER_UPLOAD = 300;
const DEFAULT_CONCURRENT_UPLOAD_LIMIT = 12;
const CONCURRENT_UPLOAD_LIMIT =
  Number(process.env.REACT_APP_ALBUM_UPLOAD_CONCURRENCY) ||
  DEFAULT_CONCURRENT_UPLOAD_LIMIT;
const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/jpg',
  'image/webp',
];

// 提取文件名中的数字部分
const extractNumber = (fileName: string) => {
  const match = fileName.match(/(?:_)?(\d+)\./);
  return match ? parseInt(match[1], 10) : 0;
};

// 排序函数 - 根据文件名中的数字排序
const sortByFileName = <T extends { relativePath: string }>(list: T[]): T[] => {
  return list.sort((a, b) => {
    const aFileName = a.relativePath.split('/').pop() || '';
    const bFileName = b.relativePath.split('/').pop() || '';
    return extractNumber(aFileName) - extractNumber(bFileName);
  });
};

const sanitizeRelativePath = (rawPath: string) => {
  return rawPath.replace(/\\/g, '/').replace(/^\/+/, '');
};

const normalizeRelativePath = (rawPath?: string) => {
  if (!rawPath) {
    return '';
  }

  return sanitizeRelativePath(rawPath);
};

const readAllDirectoryEntries = (
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> => {
  return new Promise((resolve, reject) => {
    const entries: FileSystemEntry[] = [];

    const readBatch = () => {
      reader.readEntries(
        (batch) => {
          if (!batch.length) {
            resolve(entries);
            return;
          }

          entries.push(...batch);
          readBatch();
        },
        (error) => reject(error),
      );
    };

    readBatch();
  });
};

const traverseFileTree = async (
  entry: FileSystemEntry,
): Promise<FileCandidate[]> => {
  if (entry.isFile) {
    return new Promise((resolve, reject) => {
      (entry as FileSystemFileEntry).file(
        (file) => {
          // 过滤非图片文件或不支持的图片格式
          if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            resolve([]);
            return;
          }
          resolve([
            {
              file,
              relativePath: entry.fullPath || file.name,
            },
          ]);
        },
        (error) => reject(error),
      );
    });
  }

  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const entries = await readAllDirectoryEntries(reader);
    const childFiles = await Promise.all(entries.map(traverseFileTree));
    return childFiles.flat();
  }

  return [];
};

const collectFilesFromItems = async (
  items: DataTransferItemList,
): Promise<FileCandidate[]> => {
  const tasks = Array.from(items)
    .filter((item) => item.kind === 'file')
    .map(async (item) => {
      const entry = (item as DataTransferItemWithEntry).webkitGetAsEntry?.();
      if (entry) {
        return traverseFileTree(entry);
      }

      const file = item.getAsFile();
      if (!file) {
        return [];
      }

      // 过滤非图片文件或不支持的图片格式
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        return [];
      }

      return [
        {
          file,
          relativePath: file.name,
        },
      ];
    });

  const nested = await Promise.all(tasks);
  return nested.flat();
};

const partitionCandidates = (candidates: FileCandidate[]) => {
  const valid: RelativeFile[] = [];
  const invalidFolders = new Set<string>();
  let missingFolderCount = 0;

  candidates.forEach(({ file, relativePath }) => {
    const sanitized = normalizeRelativePath(relativePath);
    if (!sanitized) {
      missingFolderCount += 1;
      return;
    }

    const parts = sanitized.split('/');
    if (parts.length < 2) {
      missingFolderCount += 1;
      return;
    }

    const folderName = parts[0];
    if (!DIGIT_FOLDER_REG.test(folderName)) {
      invalidFolders.add(folderName);
      return;
    }

    valid.push({
      file,
      relativePath: sanitized,
      folderName,
    });
  });

  return {
    valid,
    invalidFolders: Array.from(invalidFolders),
    missingFolderCount,
  };
};

const partitionInternalCandidates = (candidates: FileCandidate[]) => {
  const valid: RelativeFile[] = [];
  const invalidFolders = new Set<string>();
  let missingFolderCount = 0;

  candidates.forEach(({ file, relativePath }) => {
    const sanitized = normalizeRelativePath(relativePath);
    if (!sanitized) {
      missingFolderCount += 1;
      return;
    }

    const parts = sanitized.split('/');
    if (parts.length < 2) {
      missingFolderCount += 1;
      return;
    }

    const folderName = parts[0];
    const match = folderName.match(INTERNAL_FOLDER_REG);
    if (!match) {
      invalidFolders.add(folderName);
      return;
    }

    valid.push({
      file,
      relativePath: sanitized,
      folderName,
    });
  });

  return {
    valid,
    invalidFolders: Array.from(invalidFolders),
    missingFolderCount,
  };
};

const extractInternalProductId = (folderName: string) => {
  const match = folderName.match(INTERNAL_FOLDER_REG);
  if (!match) return NaN;
  return Number(match[1]);
};

const uploadFilesWithConcurrencyLimit = async <T,>(
  tasks: (() => Promise<T>)[],
  limit: number,
  onProgress?: (current: number, total: number) => void,
): Promise<T[]> => {
  const results: T[] = new Array(tasks.length);
  let completed = 0;
  const total = tasks.length;
  const executing: Promise<void>[] = [];

  for (let index = 0; index < tasks.length; index++) {
    const task = tasks[index];
    const currentIndex = index; // 捕获当前索引

    // 创建一个包装 promise，执行任务并保存结果
    // eslint-disable-next-line no-loop-func
    const wrappedPromise = task().then((result: T) => {
      results[currentIndex] = result;
      completed++;
      onProgress?.(completed, total);
    });

    // 创建一个自清理的 promise
    const executePromise = wrappedPromise.then(() => {
      // 任务完成后，从执行队列中移除自己
      const idx = executing.indexOf(executePromise);
      if (idx !== -1) {
        executing.splice(idx, 1);
      }
    });

    executing.push(executePromise);

    // 当达到并发限制时，等待任意一个完成
    if (executing.length >= limit) {
      await Promise.race(executing);
    }
  }

  // 等待所有剩余任务完成
  await Promise.all(executing);
  return results;
};

const ProductAlbumBatchUpload = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const internalFileInputRef = useRef<HTMLInputElement | null>(null);
  const [folderFileMap, setFolderFileMap] = useState<FolderFileMap>({});
  const [internalFolderFileMap, setInternalFolderFileMap] =
    useState<FolderFileMap>({});
  const [dragActive, setDragActive] = useState(false);
  const [internalDragActive, setInternalDragActive] = useState(false);
  const [processingDrop, setProcessingDrop] = useState(false);
  const [processingInternalDrop, setProcessingInternalDrop] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [internalUploading, setInternalUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [uploadSummary, setUploadSummary] = useState<UploadSummary>();
  const [internalUploadSummary, setInternalUploadSummary] =
    useState<UploadSummary>();
  const [activeSection, setActiveSection] = useState<
    'external' | 'internal' | null
  >(null);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [internalUploadProgress, setInternalUploadProgress] = useState({
    current: 0,
    total: 0,
  });

  const folderList = useMemo<FolderRecord[]>(() => {
    return Object.entries(folderFileMap)
      .map(([folderName, fileMap]) => ({
        folderName,
        files: sortByFileName(Object.values(fileMap)),
      }))
      .sort((a, b) => {
        return a.folderName.localeCompare(b.folderName, undefined, {
          numeric: true,
        });
      });
  }, [folderFileMap]);

  const totalFileCount = useMemo(() => {
    return folderList.reduce((sum, item) => sum + item.files.length, 0);
  }, [folderList]);

  const internalFolderList = useMemo<FolderRecord[]>(() => {
    return Object.entries(internalFolderFileMap)
      .map(([folderName, fileMap]) => ({
        folderName,
        files: sortByFileName(Object.values(fileMap)),
      }))
      .sort((a, b) => {
        return a.folderName.localeCompare(b.folderName, undefined, {
          numeric: true,
        });
      });
  }, [internalFolderFileMap]);

  const internalTotalFileCount = useMemo(() => {
    return internalFolderList.reduce((sum, item) => sum + item.files.length, 0);
  }, [internalFolderList]);

  const setDirectoryInputRef = useCallback((node: HTMLInputElement | null) => {
    if (node) {
      fileInputRef.current = node;
    }
  }, []);

  const setInternalDirectoryInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (node) {
        internalFileInputRef.current = node;
      }
    },
    [],
  );

  const appendRelativeFiles = useCallback(
    (incoming: RelativeFile[]) => {
      if (!incoming.length) {
        return;
      }

      let addedCount = 0;
      setFolderFileMap((prev) => {
        const currentTotal = Object.values(prev).reduce(
          (sum, fileMap) => sum + Object.keys(fileMap).length,
          0,
        );

        if (currentTotal >= MAX_FILES_PER_UPLOAD) {
          message.warning(
            t('album_batch_upload_limit_reached', {
              limit: MAX_FILES_PER_UPLOAD,
            }),
          );
          return prev;
        }

        const remainingSlots = MAX_FILES_PER_UPLOAD - currentTotal;
        const accepted = incoming.slice(0, remainingSlots);

        if (accepted.length < incoming.length) {
          message.warning(
            t('album_batch_upload_limit_partial', {
              limit: MAX_FILES_PER_UPLOAD,
            }),
          );
        }

        if (!accepted.length) {
          return prev;
        }

        const next: FolderFileMap = { ...prev };

        accepted.forEach((item) => {
          const existing = next[item.folderName] || {};
          next[item.folderName] = {
            ...existing,
            [item.relativePath]: item,
          };
        });

        addedCount = accepted.length;

        return next;
      });

      if (addedCount) {
        setUploadSummary(undefined);
        setActiveSection('external');
      }
    },
    [t],
  );

  const appendInternalRelativeFiles = useCallback(
    (incoming: RelativeFile[]) => {
      if (!incoming.length) {
        return;
      }

      let addedCount = 0;
      setInternalFolderFileMap((prev) => {
        const currentTotal = Object.values(prev).reduce(
          (sum, fileMap) => sum + Object.keys(fileMap).length,
          0,
        );

        if (currentTotal >= MAX_FILES_PER_UPLOAD) {
          message.warning(
            t('internal_batch_upload_limit_reached', {
              limit: MAX_FILES_PER_UPLOAD,
            }),
          );
          return prev;
        }

        const remainingSlots = MAX_FILES_PER_UPLOAD - currentTotal;
        const accepted = incoming.slice(0, remainingSlots);

        if (accepted.length < incoming.length) {
          message.warning(
            t('internal_batch_upload_limit_partial', {
              limit: MAX_FILES_PER_UPLOAD,
            }),
          );
        }

        if (!accepted.length) {
          return prev;
        }

        const next: FolderFileMap = { ...prev };

        accepted.forEach((item) => {
          const existing = next[item.folderName] || {};
          next[item.folderName] = {
            ...existing,
            [item.relativePath]: item,
          };
        });

        addedCount = accepted.length;

        return next;
      });

      if (addedCount) {
        setInternalUploadSummary(undefined);
        setActiveSection('internal');
      }
    },
    [t],
  );

  useEffect(() => {
    const hasExternal = folderList.length > 0;
    const hasInternal = internalFolderList.length > 0;

    if (!hasExternal && !hasInternal && activeSection !== null) {
      setActiveSection(null);
      return;
    }

    if (activeSection === null) {
      if (hasExternal && !hasInternal) {
        setActiveSection('external');
        return;
      }

      if (hasInternal && !hasExternal) {
        setActiveSection('internal');
      }
    }
  }, [activeSection, folderList.length, internalFolderList.length]);

  const showExternal = activeSection === null || activeSection === 'external';
  const showInternal = activeSection === null || activeSection === 'internal';

  const handleCandidates = useCallback(
    async (candidates: FileCandidate[]) => {
      if (!candidates.length) return;

      const { valid, invalidFolders, missingFolderCount } =
        partitionCandidates(candidates);

      if (invalidFolders.length) {
        message.error(
          t('album_batch_upload_invalid_folder', {
            folders: invalidFolders.join(', '),
          }),
        );
      }

      if (missingFolderCount) {
        message.error(
          t('album_batch_upload_missing_folder', { count: missingFolderCount }),
        );
      }

      if (!valid.length) {
        return;
      }

      // 验证商品是否存在
      setValidating(true);
      try {
        // 获取所有唯一的商品ID
        const uniqueProductIds = Array.from(
          new Set(valid.map((item) => item.folderName)),
        );

        // 批量验证商品是否存在
        const validationResults = await Promise.allSettled(
          uniqueProductIds.map((productId) =>
            getProductUpdateInfoNew(Number(productId)),
          ),
        );

        // 找出不存在的商品ID
        const nonExistentProductIds: string[] = [];
        const existingProductIds = new Set<string>();

        validationResults.forEach((result, index) => {
          const productId = uniqueProductIds[index];
          // 检查 promise 状态和返回数据是否有效
          if (
            result.status === 'fulfilled' &&
            result.value &&
            result.value.id
          ) {
            existingProductIds.add(productId);
          } else {
            nonExistentProductIds.push(productId);
          }
        });

        // 过滤出存在的商品对应的文件
        const validFiles = valid.filter((item) =>
          existingProductIds.has(item.folderName),
        );

        // 如果有不存在的商品,给用户提示
        if (nonExistentProductIds.length) {
          message.warning(
            t('album_batch_upload_product_not_exist', {
              ids: nonExistentProductIds.join(', '),
            }),
          );
        }

        // 添加验证通过的文件
        appendRelativeFiles(validFiles);
      } catch (error) {
        console.error('Product validation failed:', error);
        message.error(t('album_batch_upload_validation_error'));
      } finally {
        setValidating(false);
      }
    },
    [appendRelativeFiles, t],
  );

  const handleInternalCandidates = useCallback(
    async (candidates: FileCandidate[]) => {
      if (!candidates.length) return;

      const { valid, invalidFolders, missingFolderCount } =
        partitionInternalCandidates(candidates);

      if (invalidFolders.length) {
        message.error(
          `文件夹命名需为“元画像_商品编号”，不符合规则：${invalidFolders.join(', ')}`,
        );
      }

      if (missingFolderCount) {
        message.error(
          t('internal_batch_upload_missing_folder', {
            count: missingFolderCount,
          }),
        );
      }

      if (!valid.length) {
        return;
      }

      appendInternalRelativeFiles(valid);
    },
    [appendInternalRelativeFiles, t],
  );

  const handleInputChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (!files?.length) return;

      const candidates: FileCandidate[] = Array.from(files)
        .filter((file) => ACCEPTED_IMAGE_TYPES.includes(file.type))
        .map((file) => ({
          file,
          relativePath:
            (file as File & { webkitRelativePath?: string })
              .webkitRelativePath || file.name,
        }));

      await handleCandidates(candidates);
      event.target.value = '';
    },
    [handleCandidates],
  );

  const handleInternalInputChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (!files?.length) return;

      const candidates: FileCandidate[] = Array.from(files)
        .filter((file) => ACCEPTED_IMAGE_TYPES.includes(file.type))
        .map((file) => ({
          file,
          relativePath:
            (file as File & { webkitRelativePath?: string })
              .webkitRelativePath || file.name,
        }));

      await handleInternalCandidates(candidates);
      event.target.value = '';
    },
    [handleInternalCandidates],
  );

  const handleDrop = useCallback(
    async (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragActive(false);

      if (!event.dataTransfer?.items?.length) {
        return;
      }

      setProcessingDrop(true);
      try {
        const candidates = await collectFilesFromItems(
          event.dataTransfer.items,
        );
        await handleCandidates(candidates);
      } catch (error) {
        console.error(error);
        message.error(t('album_batch_upload_drop_error'));
      } finally {
        setProcessingDrop(false);
      }
    },
    [handleCandidates, t],
  );

  const handleInternalDrop = useCallback(
    async (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setInternalDragActive(false);

      if (!event.dataTransfer?.items?.length) {
        return;
      }

      setProcessingInternalDrop(true);
      try {
        const candidates = await collectFilesFromItems(
          event.dataTransfer.items,
        );
        await handleInternalCandidates(candidates);
      } catch (error) {
        console.error(error);
        message.error(t('internal_batch_upload_drop_error'));
      } finally {
        setProcessingInternalDrop(false);
      }
    },
    [handleInternalCandidates, t],
  );

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(true);
  }, []);

  const handleInternalDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setInternalDragActive(true);
    },
    [],
  );

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
      setDragActive(false);
    }
  }, []);

  const handleInternalDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      const nextTarget = event.relatedTarget as Node | null;
      if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
        setInternalDragActive(false);
      }
    },
    [],
  );

  const clearFolder = useCallback((folderName: string) => {
    let removed = false;
    setFolderFileMap((prev) => {
      if (!prev[folderName]) {
        return prev;
      }

      const next = { ...prev };
      delete next[folderName];
      removed = true;
      return next;
    });

    if (removed) {
      setUploadSummary(undefined);
    }
  }, []);

  const clearAll = useCallback(() => {
    if (!folderList.length) return;
    setFolderFileMap({});
    setUploadSummary(undefined);
  }, [folderList.length]);

  const clearInternalFolder = useCallback((folderName: string) => {
    let removed = false;
    setInternalFolderFileMap((prev) => {
      if (!prev[folderName]) {
        return prev;
      }

      const next = { ...prev };
      delete next[folderName];
      removed = true;
      return next;
    });

    if (removed) {
      setInternalUploadSummary(undefined);
    }
  }, []);

  const clearInternalAll = useCallback(() => {
    if (!internalFolderList.length) return;
    setInternalFolderFileMap({});
    setInternalUploadSummary(undefined);
  }, [internalFolderList.length]);

  const handleSectionSwitch = useCallback(() => {
    if (!activeSection) return;

    const target = activeSection === 'external' ? 'internal' : 'external';
    const isBusy =
      uploading ||
      internalUploading ||
      processingDrop ||
      processingInternalDrop ||
      validating;

    if (isBusy) {
      message.warning(t('album_batch_upload_switch_busy'));
      return;
    }

    Modal.confirm({
      title: t('album_batch_upload_switch_confirm_title'),
      content: t('album_batch_upload_switch_confirm_content'),
      okText: t('confirm'),
      cancelText: t('cancel'),
      onOk: () => {
        if (activeSection === 'external') {
          setFolderFileMap({});
          setUploadSummary(undefined);
          setUploadProgress({ current: 0, total: 0 });
        } else {
          setInternalFolderFileMap({});
          setInternalUploadSummary(undefined);
          setInternalUploadProgress({ current: 0, total: 0 });
        }

        setActiveSection(target);
      },
    });
  }, [
    activeSection,
    internalUploading,
    processingDrop,
    processingInternalDrop,
    t,
    uploading,
    validating,
  ]);

  const handleUpload = useCallback(async () => {
    if (!folderList.length) {
      message.warning(t('album_batch_upload_empty_warning'));
      return;
    }

    setUploading(true);
    try {
      const localFailures: UploadFailure[] = [];
      const preparedItems: {
        folderName: string;
        payload: ProductAlbumBatchUploadPayload;
      }[] = [];
      const folderMetaMap = new Map<
        string,
        { productId: number; urls: string[]; errors: string[] }
      >();
      const uploadTasks: (() => Promise<UploadTaskOutcome>)[] = [];

      folderList.forEach((record) => {
        const productId = Number(record.folderName);
        if (!Number.isFinite(productId)) {
          localFailures.push({
            folderName: record.folderName,
            reason: String(t('album_batch_upload_file_upload_failed_reason')),
          });
          return;
        }

        folderMetaMap.set(record.folderName, {
          productId,
          urls: Array(record.files.length).fill(''),
          errors: [],
        });

        record.files.forEach(({ file }, fileIndex) => {
          uploadTasks.push(() =>
            commonApi
              .uploadProductImage(file)
              .then((result) => ({
                folderName: record.folderName,
                fileIndex,
                url: result.url,
              }))
              .catch((error) => {
                console.error(
                  `Failed to upload file ${record.folderName}/${file.name}`,
                  error,
                );
                return {
                  folderName: record.folderName,
                  fileIndex,
                  error: String(
                    t('album_batch_upload_file_upload_failed_reason'),
                  ),
                };
              }),
          );
        });
      });

      if (!uploadTasks.length) {
        message.error(t('album_batch_upload_prepare_failed'));
        setUploadSummary({
          failures: localFailures,
          successFolders: [],
        });
        return;
      }

      const totalUploads = uploadTasks.length;
      setUploadProgress({ current: 0, total: totalUploads });

      const effectiveLimit = Math.max(
        1,
        Math.min(CONCURRENT_UPLOAD_LIMIT, totalUploads),
      );

      const uploadResults = await uploadFilesWithConcurrencyLimit(
        uploadTasks,
        effectiveLimit,
        (current) => {
          setUploadProgress({
            current,
            total: totalUploads,
          });
        },
      );

      uploadResults.forEach((result) => {
        const meta = folderMetaMap.get(result.folderName);
        if (!meta) {
          return;
        }

        if (result.url) {
          meta.urls[result.fileIndex] = result.url;
        } else if (result.error) {
          meta.errors.push(result.error);
        }
      });

      folderMetaMap.forEach((meta, folderName) => {
        const { productId, urls, errors } = meta;
        const isComplete = urls.every((item) => !!item);

        if (!isComplete) {
          localFailures.push({
            folderName,
            reason:
              errors[0] ||
              String(t('album_batch_upload_file_upload_failed_reason')),
          });
          return;
        }

        preparedItems.push({
          folderName,
          payload: {
            productId,
            albumPics: urls.join(','),
          },
        });
      });

      if (!preparedItems.length) {
        message.error(t('album_batch_upload_prepare_failed'));
        setUploadSummary({
          failures: localFailures,
          successFolders: [],
        });
        return;
      }

      await batchUploadProductAlbumPictures(
        preparedItems.map((item) => item.payload),
      );

      const successFolders = preparedItems.map((item) => item.folderName);

      setUploadSummary({
        failures: localFailures,
        successFolders,
      });

      if (!localFailures.length) {
        message.success(t('album_batch_upload_all_success'));
        setFolderFileMap({});
      } else {
        message.warning(
          t('album_batch_upload_partial_failed', {
            count: localFailures.length,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      message.error(t('album_batch_upload_request_error'));
    } finally {
      setUploading(false);
    }
  }, [folderList, t]);

  const handleInternalUpload = useCallback(async () => {
    if (!internalFolderList.length) {
      message.warning(t('internal_batch_upload_empty_warning'));
      return;
    }

    setInternalUploading(true);
    try {
      const localFailures: UploadFailure[] = [];
      const preparedItems: {
        folderName: string;
        payload: ProductAlbumBatchUploadPayload;
      }[] = [];
      const folderMetaMap = new Map<
        string,
        { productId: number; keys: string[]; errors: string[] }
      >();
      const uploadTasks: (() => Promise<UploadTaskOutcome>)[] = [];

      internalFolderList.forEach((record) => {
        const productId = extractInternalProductId(record.folderName);
        if (!Number.isFinite(productId)) {
          localFailures.push({
            folderName: record.folderName,
            reason: '文件夹命名不符合规则，无法提取商品编号',
          });
          return;
        }

        folderMetaMap.set(record.folderName, {
          productId,
          keys: Array(record.files.length).fill(''),
          errors: [],
        });

        record.files.forEach(({ file }, fileIndex) => {
          uploadTasks.push(() =>
            commonApi
              .uploadFile(file, true, {
                compress: true,
                quality: 80,
                maxSize: 0,
              })
              .then((result) => ({
                folderName: record.folderName,
                fileIndex,
                url: result.key,
              }))
              .catch((error) => {
            console.error(
              `Failed to upload internal file ${record.folderName}/${file.name}`,
              error,
            );
            return {
              folderName: record.folderName,
              fileIndex,
              error: String(t('internal_batch_upload_file_upload_failed_reason')),
            };
          }),
      );
    });
  });

      if (!uploadTasks.length) {
        message.error(t('internal_batch_upload_prepare_failed'));
        setInternalUploadSummary({
          failures: localFailures,
          successFolders: [],
        });
        return;
      }

      const totalUploads = uploadTasks.length;
      setInternalUploadProgress({ current: 0, total: totalUploads });

      const effectiveLimit = Math.max(
        1,
        Math.min(CONCURRENT_UPLOAD_LIMIT, totalUploads),
      );

      const uploadResults = await uploadFilesWithConcurrencyLimit(
        uploadTasks,
        effectiveLimit,
        (current) => {
          setInternalUploadProgress({
            current,
            total: totalUploads,
          });
        },
      );

      uploadResults.forEach((result) => {
        const meta = folderMetaMap.get(result.folderName);
        if (!meta) {
          return;
        }

        if (result.url) {
          meta.keys[result.fileIndex] = result.url;
        } else if (result.error) {
          meta.errors.push(result.error);
        }
      });

      folderMetaMap.forEach((meta, folderName) => {
        const { productId, keys, errors } = meta;
        const isComplete = keys.every((item) => !!item);

        if (!isComplete) {
          localFailures.push({
            folderName,
            reason:
              errors[0] || t('internal_batch_upload_file_upload_failed_reason'),
          });
          return;
        }

        preparedItems.push({
          folderName,
          payload: {
            productId,
            internalImages: keys.join(','),
          },
        });
      });

      if (!preparedItems.length) {
        message.error(t('internal_batch_upload_prepare_failed'));
        setInternalUploadSummary({
          failures: localFailures,
          successFolders: [],
        });
        return;
      }

      await batchUploadProductAlbumPictures(
        preparedItems.map((item) => item.payload),
      );

      const successFolders = preparedItems.map((item) => item.folderName);

      setInternalUploadSummary({
        failures: localFailures,
        successFolders,
      });

      if (!localFailures.length) {
        message.success(t('internal_batch_upload_all_success'));
        setInternalFolderFileMap({});
      } else {
        message.warning(
          t('internal_batch_upload_partial_failed', {
            count: localFailures.length,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      message.error(t('internal_batch_upload_request_error'));
    } finally {
      setInternalUploading(false);
    }
  }, [internalFolderList, t]);

  const columns: ColumnsType<FolderRecord> = [
    {
      title: t('album_batch_upload_folder_column'),
      dataIndex: 'folderName',
      key: 'folderName',
      width: 160,
    },
    {
      title: t('album_batch_upload_file_count_column'),
      dataIndex: 'files',
      key: 'files',
      width: 140,
      render: (files: RelativeFile[]) => files.length,
    },
    {
      title: t('options'),
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          danger
          onClick={() => clearFolder(record.folderName)}
        >
          {t('delete')}
        </Button>
      ),
    },
  ];

  const internalColumns: ColumnsType<FolderRecord> = [
    {
      title: '文件夹 (元画像_商品编号)',
      dataIndex: 'folderName',
      key: 'folderName',
      width: 200,
    },
    {
      title: '文件数',
      dataIndex: 'files',
      key: 'files',
      width: 120,
      render: (files: RelativeFile[]) => files.length,
    },
    {
      title: t('options'),
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          danger
          onClick={() => clearInternalFolder(record.folderName)}
        >
          {t('delete')}
        </Button>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-4">
      {(activeSection === 'external' || activeSection === 'internal') && (
        <div className="flex justify-end">
          <Button
            type="link"
            size="small"
            onClick={handleSectionSwitch}
          >
            {activeSection === 'external'
              ? t('album_batch_upload_switch_to_internal')
              : t('album_batch_upload_switch_to_external')}
          </Button>
        </div>
      )}

      {showExternal && (
        <>
          <div className="flex items-center justify-between">
            <Typography.Title level={3} className="!mb-0">
              {t('album_batch_upload')}
            </Typography.Title>
            <span className="text-sm text-gray-500">
              {t('album_batch_upload_tip_rule')} (max: {MAX_FILES_PER_UPLOAD})
            </span>
          </div>

          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-all ${
              dragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 bg-gray-50'
            }`}
          >
            <InboxOutlined className="text-5xl text-gray-400 mb-2" />
            <p className="text-base text-gray-600 mb-3">
              {t('album_batch_upload_drag_hint')}
            </p>
            <Button
              icon={<UploadOutlined />}
              onClick={() => fileInputRef.current?.click()}
              disabled={processingDrop || validating}
            >
              {t('album_batch_upload_select_folder')}
            </Button>
            <input
              ref={setDirectoryInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleInputChange}
              {...({ webkitdirectory: '', directory: '' } as any)}
            />
            {processingDrop || validating ? (
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                <Spin size="small" />
                <span>
                  {validating
                    ? t('album_batch_upload_validating')
                    : t('album_batch_upload_processing')}
                </span>
              </div>
            ) : null}
          </div>

          {folderList.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {totalFileCount} {t('album_batch_upload_file_count_column')}
                </span>
                <div className="flex gap-2">
                  <Button size="small" onClick={clearAll}>
                    {t('album_batch_upload_clear_all')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    icon={<UploadOutlined />}
                    onClick={handleUpload}
                    disabled={uploading || processingDrop}
                    loading={uploading}
                  >
                    {t('album_batch_upload_start')}
                  </Button>
                </div>
              </div>

              <Table
                size="small"
                columns={columns}
                dataSource={folderList}
                rowKey={(record) => record.folderName}
                pagination={false}
              />
            </>
          )}

          {uploading && uploadProgress.total > 0 && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Uploading files...
                </span>
                <span className="text-sm text-gray-600">
                  {uploadProgress.current} / {uploadProgress.total}
                </span>
              </div>
              <Progress
                percent={Math.round(
                  (uploadProgress.current / uploadProgress.total) * 100,
                )}
                status="active"
                strokeColor={{
                  '0%': '#1890ff',
                  '100%': '#52c41a',
                }}
              />
            </div>
          )}

          {uploadSummary && uploadSummary.failures.length > 0 && (
            <Alert
              type="error"
              showIcon
              message={`${uploadSummary.failures.length} ${t('album_batch_upload_failure_summary')}`}
              description={
                <Table
                  size="small"
                  pagination={false}
                  showHeader={false}
                  rowKey={(_, index) => String(index)}
                  columns={[
                    {
                      dataIndex: 'folderName',
                      width: 100,
                      render: (value: string) => <Tag color="error">{value}</Tag>,
                    },
                    {
                      dataIndex: 'reason',
                      render: (value: string) => (
                        <span className="text-xs text-gray-600">{value}</span>
                      ),
                    },
                  ]}
                  dataSource={uploadSummary.failures}
                />
              }
            />
          )}
        </>
      )}

      {showInternal && (
        <div className="mt-8 space-y-4">
          <div className="flex items-center justify-between">
            <Typography.Title level={4} className="!mb-0">
              {t('internal_batch_upload')}
            </Typography.Title>
            <span className="text-sm text-gray-500">
              {t('internal_batch_upload_tip_rule')} (max: {MAX_FILES_PER_UPLOAD})
            </span>
          </div>

          <div
            onDrop={handleInternalDrop}
            onDragOver={handleInternalDragOver}
            onDragLeave={handleInternalDragLeave}
            className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-all ${
              internalDragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 bg-gray-50'
            }`}
          >
            <InboxOutlined className="text-5xl text-gray-400 mb-2" />
            <p className="text-base text-gray-600 mb-3">
              {t('internal_batch_upload_drag_hint')}
            </p>
            <Button
              icon={<UploadOutlined />}
              onClick={() => internalFileInputRef.current?.click()}
              disabled={processingInternalDrop}
            >
              {t('internal_batch_upload_select_folder')}
            </Button>
            <input
              ref={setInternalDirectoryInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleInternalInputChange}
              {...({ webkitdirectory: '', directory: '' } as any)}
            />
            {processingInternalDrop ? (
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                <Spin size="small" />
                <span>{t('internal_batch_upload_processing')}</span>
              </div>
            ) : null}
          </div>

          {internalFolderList.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {t('internal_batch_upload_file_count', {
                    count: internalTotalFileCount,
                  })}
                </span>
                <div className="flex gap-2">
                  <Button size="small" onClick={clearInternalAll}>
                    {t('clear')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    icon={<UploadOutlined />}
                    onClick={handleInternalUpload}
                    disabled={internalUploading || processingInternalDrop}
                    loading={internalUploading}
                  >
                    {t('internal_batch_upload_start')}
                  </Button>
                </div>
              </div>

              <Table
                size="small"
                columns={internalColumns}
                dataSource={internalFolderList}
                rowKey={(record) => record.folderName}
                pagination={false}
              />
            </>
          )}

          {internalUploading && internalUploadProgress.total > 0 && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  {t('internal_batch_upload_uploading')}
                </span>
                <span className="text-sm text-gray-600">
                  {internalUploadProgress.current} / {internalUploadProgress.total}
                </span>
              </div>
              <Progress
                percent={Math.round(
                  (internalUploadProgress.current / internalUploadProgress.total) *
                    100,
                )}
                status="active"
                strokeColor={{
                  '0%': '#1890ff',
                  '100%': '#52c41a',
                }}
              />
            </div>
          )}

          {internalUploadSummary && internalUploadSummary.failures.length > 0 && (
            <Alert
              type="error"
              showIcon
              message={`${internalUploadSummary.failures.length} 个文件夹上传失败`}
              description={
                <Table
                  size="small"
                  pagination={false}
                  showHeader={false}
                  rowKey={(_, index) => String(index)}
                  columns={[
                    {
                      dataIndex: 'folderName',
                      width: 100,
                      render: (value: string) => <Tag color="error">{value}</Tag>,
                    },
                    {
                      dataIndex: 'reason',
                      render: (value: string) => (
                        <span className="text-xs text-gray-600">{value}</span>
                      ),
                    },
                  ]}
                  dataSource={internalUploadSummary.failures}
                />
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ProductAlbumBatchUpload;
